import type { ModuleChatStatus } from "../contract";
import { TEXT_COMMAND_TIER_CHAT_STATUSES, type TextCommandMinimumTier } from "../contracts";
import { renderTemplate } from "../contract";
import { chatStatusMeetsMinimumTier } from "../../contract";

export const COMMAND_NAME_PATTERN = /^[a-z0-9][a-z0-9_-]{0,31}$/;

export type TextCommandInput =
  | { kind: "command"; name: string; arguments?: string }
  | { kind: "unknown" };

export const validCommandName = (name: string): boolean => COMMAND_NAME_PATTERN.test(name);

export const normalizeCommandName = (name: string): string => name.trim().replace(/^!/u, "").toLowerCase();

export const commandFromMessage = (message: string): TextCommandInput | null => {
  const trimmed = message.trim();
  const firstWord = trimmed.split(/\s+/u)[0];
  if (firstWord === undefined || !firstWord.startsWith("!")) return null;
  const name = firstWord.slice(1).toLowerCase();
  if (!validCommandName(name)) return { kind: "unknown" };
  // Spelled out rather than shorthand: the key travels into
  // `event_log.detail_json`, so renaming the local would rename the stored key
  // with it. That is exactly how this broke once already.
  const commandArguments = trimmed.slice(firstWord.length).trim();
  return {
    kind: "command",
    name,
    ...(commandArguments.length === 0 ? {} : { arguments: commandArguments }),
  };
};

export const renderCommandText = (
  text: string,
  values: Readonly<Record<string, string | number>>,
): string => renderTemplate(text, values);

export const cooldownRemaining = (lastUsedAt: string | null, now: string, cooldownSeconds: number): number => {
  if (lastUsedAt === null) return 0;
  const elapsed = Date.parse(now) - Date.parse(lastUsedAt);
  if (!Number.isFinite(elapsed) || elapsed < 0) return cooldownSeconds;
  return Math.max(0, Math.ceil(cooldownSeconds - elapsed / 1000));
};

/** Use the immutable creation time as the first CAS version so a recreated
 * name cannot match a stale editor that still holds the old command's revision. */
export const initialTextCommandRevision = (createdAt: string): number => {
  const timestamp = Date.parse(createdAt);
  return Number.isSafeInteger(timestamp) && timestamp >= 1 ? timestamp : 1;
};

/**
 * The tiers are deliberately not a numeric ladder. The status list can
 * contain multiple badges: moderator and broadcaster also satisfy
 * "subscriber" and "VIP", but a VIP does not satisfy "subscriber".
 */
export const statusForTier = TEXT_COMMAND_TIER_CHAT_STATUSES;

export const chatStatusMeetsTier = chatStatusMeetsMinimumTier as (
  status: readonly ModuleChatStatus[] | null,
  minimumTier: TextCommandMinimumTier,
) => boolean;
